/**
 * Ribbon handlers for v2 workstation tools.
 *
 * Case-stage tools open the Pipeline protocol. Image ops stay on Study.
 * Quantification tools open the Quantify workspace.
 */

import { openProtocolStep } from "@/pipeline/protocol";
import { registerAction } from "@/shell/actions";
import { appActions, useAppStore } from "@/store/appStore";
import { TOOLS, type ToolTabId } from "./catalog";
import { useToolSession } from "./session";

export function openQuantifyWorkspace(focusId?: string): void {
  if (focusId) useToolSession.getState().selectTool(focusId);
  appActions.setActiveTab("quantification");
  appActions.togglePanel("right", false);
  appActions.togglePanel("left", false);
}

export function selectTool(id: string, opts?: { launch?: boolean }): void {
  const tool = TOOLS.find((t) => t.id === id);
  if (!tool || tool.status === "future") return;
  if (tool.stage) {
    useToolSession.setState({ selectedId: null });
    openProtocolStep(tool.stage.modality, tool.stage.id);
    return;
  }
  if (tool.tab === "quantification" || tool.inspect === "results") {
    openQuantifyWorkspace(id);
    return;
  }
  useToolSession.getState().selectTool(id);
  appActions.setActiveTab("view");
  appActions.togglePanel("right", false);
  const job = useAppStore.getState().job;
  if (!job || !["running", "queued"].includes(job.state)) appActions.togglePanel("bottom", true);
  if (opts?.launch === true) useToolSession.getState().openLaunch();
}

export const TOOL_ACTIONS: Record<string, () => void> = {
  "tool.run": () => void useToolSession.getState().runSelected(),
};

for (const tool of TOOLS) {
  TOOL_ACTIONS[`tool.select.${tool.id}`] = () => selectTool(tool.id);
}

/** Home quick-tools from planchat.md §3. */
TOOL_ACTIONS["home.quick.brainMask"] = () => selectTool("prep.brainMask");
TOOL_ACTIONS["home.quick.register"] = () => selectTool("reg.dwiMni");
TOOL_ACTIONS["home.quick.stroke"] = () => selectTool("seg.stroke");
TOOL_ACTIONS["home.quick.quantify"] = () => openQuantifyWorkspace("q.coreVolume");

export function rememberTabTool(tab: ToolTabId): void {
  useToolSession.getState().selectTabDefault(tab);
}

let registered = false;

export function registerToolActions(): void {
  if (registered) return;
  registered = true;
  for (const [name, handler] of Object.entries(TOOL_ACTIONS)) {
    registerAction(name, handler);
  }
}
